import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { NotificationManager } from 'react-notifications';
import { GetAuditLogDetail } from '../../services/AuditLogService';
import CommonLoader from '../common/CommonLoader';
import { auditTypes, formatAuditDate, formatFieldName, getAuditErrorMessage } from './auditLogUtils';

// ENB-577: the full change history of one order / sample / result, oldest first, from the first
// recorded state to the current one. Opened in a new tab from the audit list.
const AuditLogDetail = () => {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const typeInfo = auditTypes[type];

  const orderNumber = searchParams.get('orderNumber');
  const sampleNumber = searchParams.get('sampleNumber');
  const testCode = searchParams.get('testCode');
  const parameter = searchParams.get('parameter');

  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  // The triggers also log updates that changed nothing; hiding them keeps the history readable.
  const [hideNoChange, setHideNoChange] = useState(true);
  // Snapshots start collapsed; holds the sequence numbers the user has opened.
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleEntry = (sequence) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sequence)) next.delete(sequence);
      else next.add(sequence);
      return next;
    });
  };

  useEffect(() => {
    if (!typeInfo || !orderNumber) return;

    const load = async () => {
      try {
        setLoading(true);
        const response = await GetAuditLogDetail(type, orderNumber, sampleNumber, testCode, parameter);
        setDetail(response.results);
      } catch (err) {
        NotificationManager.error(await getAuditErrorMessage(err), 'Error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [type, orderNumber, sampleNumber, testCode, parameter, typeInfo]);

  if (!typeInfo || !orderNumber) {
    return (
      <div className="vlp-component-container">
        <span className="title">Unknown audit entry</span>
      </div>
    );
  }

  const entries = detail ? detail.entries.filter((e) => !(hideNoChange && e.noChanges)) : [];
  const hiddenCount = detail ? detail.entries.length - entries.length : 0;

  const heading = [
    `Order ${orderNumber}`,
    sampleNumber ? `Sample ${sampleNumber}` : null,
    testCode ? `Test ${testCode}` : null,
    parameter ? parameter : null,
  ].filter(Boolean).join(' / ');

  return (
    <div className="vlp-component-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="title">{typeInfo.title}: {heading}</span>
      </div>
      <hr />

      {detail && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`${detail.totalSnapshots} snapshot${detail.totalSnapshots === 1 ? '' : 's'}`} color="primary" variant="outlined" size="small" />
          <FormControlLabel
            control={<Switch checked={hideNoChange} onChange={(e) => setHideNoChange(e.target.checked)} />}
            label={`Hide entries with no changes${hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}`}
          />
        </Box>
      )}

      {detail && detail.truncated && (
        <Typography color="error" sx={{ mb: 2 }}>
          Only the first {detail.entries.length} of {detail.totalSnapshots} snapshots are shown.
        </Typography>
      )}

      {detail && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Snapshots without a date were recorded before date tracking started (2026-10-22). They are shown in stored order.
          Results and samples often have no user on the record, so "Record modified by" can be empty.
        </Typography>
      )}

      {detail && entries.length === 0 && (
        <Typography>No entries to show.</Typography>
      )}

      {entries.map((entry) => {
        const isOpen = expanded.has(entry.sequence);
        return (
        <Paper key={entry.sequence} sx={{ mb: 2, p: 2 }}>
          <Box
            onClick={() => toggleEntry(entry.sequence)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}
          >
            <ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            <strong>#{entry.sequence}</strong>
            <Chip label={entry.isInitial ? 'First recorded state' : entry.action} size="small" color={entry.isInitial ? 'success' : 'default'} />
            <span>{entry.changedAt ? formatAuditDate(entry.changedAt) : 'Date not recorded'}</span>
            {entry.recordModifiedBy && <span>Record modified by: {entry.recordModifiedBy}</span>}
            {entry.recordModifiedAt && <span>Record modified at: {formatAuditDate(entry.recordModifiedAt)}</span>}
            {!entry.noChanges && (
              <Typography variant="body2" color="text.secondary">
                ({entry.changes.length} field{entry.changes.length === 1 ? '' : 's'})
              </Typography>
            )}
          </Box>

          <Collapse in={isOpen} unmountOnExit>
          <Box sx={{ mt: 1 }}>
          {entry.noChanges ? (
            <Typography variant="body2" color="text.secondary">Saved with no field changes.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    {!entry.isInitial && <TableCell>Before</TableCell>}
                    <TableCell>{entry.isInitial ? 'Value' : 'After'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entry.changes.map((change) => (
                    <TableRow key={change.field}>
                      <TableCell>{formatFieldName(change.field)}</TableCell>
                      {!entry.isInitial && <TableCell>{change.original}</TableCell>}
                      <TableCell>{change.current}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </Box>
          </Collapse>
        </Paper>
        );
      })}

      <CommonLoader loading={loading} />
    </div>
  );
};

export default AuditLogDetail;
